import React from 'react';
import PropTypes from 'prop-types';
import {
  useScreenReaderAnnouncement,
  useFocusTrap,
} from '../utils/accessibilityUtils';
import './ErrorBoundary.css';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of the component tree that crashed
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.containerRef = React.createRef();
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console and any error reporting service
    console.error('Error caught by boundary:', error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // TODO: Add error reporting service integration here
    // Example: Sentry, LogRocket, etc.
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleKeyDown = event => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        if (event.target.tagName === 'BUTTON') {
          event.preventDefault();
          if (event.target.textContent.includes('Refresh')) {
            this.handleRefresh();
          } else if (event.target.textContent.includes('Home')) {
            this.handleGoHome();
          }
        }
        break;
      case 'Escape':
        // Allow users to try to recover by pressing Escape
        this.setState({ hasError: false, error: null, errorInfo: null });
        break;
      default:
        break;
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="error-boundary"
          role="alert"
          aria-live="assertive"
          aria-labelledby="error-title"
          aria-describedby="error-description"
        >
          <div
            ref={this.containerRef}
            className="error-boundary-content"
            onKeyDown={this.handleKeyDown}
            tabIndex={-1}
          >
            <div className="error-boundary-icon" aria-hidden="true">
              ⚠️
            </div>
            <h2 id="error-title">Something went wrong</h2>
            <p id="error-description">
              We&apos;re sorry, but something unexpected happened. Our team has
              been notified and is working to fix the issue.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Error Details (Development)</summary>
                <pre>{this.state.error.toString()}</pre>
                {this.state.errorInfo && (
                  <pre>{this.state.errorInfo.componentStack}</pre>
                )}
              </details>
            )}

            <div className="error-boundary-actions">
              <button
                onClick={this.handleRefresh}
                className="error-boundary-btn primary"
                aria-describedby="refresh-description"
              >
                Refresh Page
              </button>
              <div id="refresh-description" className="sr-only">
                Click to reload the page and try again
              </div>

              <button
                onClick={this.handleGoHome}
                className="error-boundary-btn secondary"
                aria-describedby="home-description"
              >
                Go to Home
              </button>
              <div id="home-description" className="sr-only">
                Click to navigate to the home page
              </div>
            </div>

            <div className="error-boundary-help">
              <p>
                <strong>Need help?</strong> You can also try:
              </p>
              <ul>
                <li>
                  Pressing <kbd>Escape</kbd> to try to recover
                </li>
                <li>Clearing your browser cache</li>
                <li>Contacting support if the problem persists</li>
              </ul>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
